import type { BetterAuthClientPlugin } from 'better-auth/client';

import {
  IMPERSONATE_PATH,
  PATH_METHODS,
  STOP_IMPERSONATING_PATH,
} from './constants';
import type { impersonation } from './index';

/**
 * Client half of the impersonation plugin.
 *
 * Gives `authClient.impersonateUser({ userId })` and
 * `authClient.stopImpersonating()`, inferred from the server plugin's type so a
 * renamed endpoint is a compile error in every front end rather than a 404 in
 * one of them.
 *
 * Whether the current session *is* impersonated is read from the session, not
 * from local storage — `session.impersonatedBy`. That matters beyond
 * bookkeeping: local state can outlive the session it describes, which is how
 * a stale flag ends up applied to a session that is no longer impersonated.
 */
export const impersonationClient = () =>
  ({
    id: 'impersonation',
    $InferServerPlugin: {} as ReturnType<typeof impersonation>,

    /**
     * Not optional. `$InferServerPlugin` is erased at build, so the client
     * proxy would otherwise guess the method from the arguments and send
     * `stopImpersonating()` — which takes none — as a GET. See `PATH_METHODS`.
     */
    pathMethods: PATH_METHODS,

    /**
     * Both endpoints replace the session cookie, so anything reading
     * `useSession` is stale the moment they return. Callers that hard-reload
     * don't notice; ones that don't would keep rendering the previous user.
     */
    atomListeners: [
      {
        matcher: (path: string) =>
          path === IMPERSONATE_PATH || path === STOP_IMPERSONATING_PATH,
        signal: '$sessionSignal',
      },
    ],
  }) satisfies BetterAuthClientPlugin;

export {
  ADMIN_SESSION_COOKIE,
  IMPERSONATE_PATH,
  PATH_METHODS,
  STOP_IMPERSONATING_PATH,
} from './constants';
